import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { X, Loader2 } from 'lucide-react';

import { cn } from 'src/utils/cn';
import { Button } from 'src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/ui/tooltip';
import { Avatar, AvatarImage, AvatarFallback } from 'src/components/ui/avatar';

import { notificationApi } from 'src/features/notifications/api';
import { useNotificationProvider } from 'src/features/notifications/providers/notification-realtime-provider';

import { NotificationType, type NotificationDto } from 'src/contracts/notification';

const isJobNotification = (typeEnum: number) =>
  typeEnum === NotificationType.JobApplyReminder ||
  typeEnum === NotificationType.InterviewReminder ||
  typeEnum === NotificationType.FollowUpReminder;

export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, fetchNotifications } =
    useNotificationProvider();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredNotification, setHoveredNotification] = useState<string | null>(null);
  const [deletingNotification, setDeletingNotification] = useState<string | null>(null);

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const handleNotificationClick = async (notification: NotificationDto) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Scrape completed notifications navigate to job market with local source
    if (notification.typeEnum === 20 && notification.relatedRunId) {
      navigate(`/job-market?runId=${notification.relatedRunId}&source=local`);
      handleCloseMenu();
      return;
    }

    // Job notifications navigate to saved jobs
    if (notification.relatedJobId) {
      navigate('/saved-jobs');
      handleCloseMenu();
      return;
    }

    // Comment notifications navigate to the comment page
    if (notification.relatedCommentId) {
      navigate(`/tree-hole/${notification.relatedCommentId}`);
      handleCloseMenu();
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // Prevent triggering the MenuItem click
    setDeletingNotification(notificationId);

    try {
      await notificationApi.deleteNotification(notificationId);
      await fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    } finally {
      setDeletingNotification(null);
    }
  };

  const getNotificationIcon = (typeEnum: number) => {
    switch (typeEnum) {
      case NotificationType.CommentReply:
        return '💬';
      case NotificationType.CommentLike:
        return '❤️';
      case NotificationType.JobApplyReminder:
        return '📋';
      case NotificationType.InterviewReminder:
        return '📅';
      case NotificationType.FollowUpReminder:
        return '🔄';
      default:
        return '🔔';
    }
  };

  const getNotificationBadge = (typeEnum: number) => {
    switch (typeEnum) {
      case NotificationType.CommentReply:
        return { label: 'Reply', colorClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' };
      case NotificationType.CommentLike:
        return { label: 'Like', colorClass: 'bg-red-500/10 text-red-600 dark:text-red-400' };
      case NotificationType.JobApplyReminder:
        return { label: 'Reminder', colorClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
      case NotificationType.InterviewReminder:
        return { label: 'Interview', colorClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
      case NotificationType.FollowUpReminder:
        return { label: 'Follow Up', colorClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
      default:
        return { label: 'Notification', colorClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' };
    }
  };

  const truncateText = (text: string, maxLength: number = 100) =>
    text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="notifications"
                className={cn(
                  'relative transition-all duration-150',
                  unreadCount > 0 && 'animate-[bellPulse_2s_infinite]'
                )}
              >
                <span className="relative inline-flex">
                  <span className="text-xl flex items-center justify-center">🔔</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-2 flex items-center justify-center min-w-[20px] h-5 px-1 text-[0.7rem] font-semibold bg-destructive text-destructive-foreground rounded-full border-2 border-background shadow-sm">
                      {unreadCount}
                    </span>
                  )}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {unreadCount > 0 ? `${unreadCount} new notifications` : 'Notifications'}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          align="end"
          sideOffset={8}
          className={cn(
            'w-[360px] max-h-[500px] overflow-y-auto p-0',
            'rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)]',
            'border border-border/60 backdrop-blur-xl',
            'bg-background/95 dark:bg-zinc-900/95'
          )}
        >
          {/* Header */}
          <div className="pt-5 pb-4 px-5 flex justify-between items-center border-b border-border/60 bg-muted/30 backdrop-blur-sm">
            <h3 className="font-bold text-[1.15rem] tracking-tight">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={loading}
                className="rounded-full px-4 py-1 text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/15 shadow-sm"
              >
                Mark all as read
              </Button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <button
                type="button"
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                onMouseEnter={() => setHoveredNotification(notification.id)}
                onMouseLeave={() => setHoveredNotification(null)}
                className={cn(
                  'w-full text-left py-3.5 px-5 min-h-[120px] border-b border-border/20',
                  'hover:bg-muted/50 transition-colors duration-200 relative',
                  !notification.isRead && 'bg-primary/[0.04]'
                )}
              >
                <div className="flex w-full items-start relative">
                  {/* Avatar: profile picture for social, icon for job notifications */}
                  {isJobNotification(notification.typeEnum) ? (
                    <div className="w-10 h-10 mr-3 flex-shrink-0 rounded-full flex items-center justify-center text-xl bg-amber-500/10 border border-amber-500/20">
                      {getNotificationIcon(notification.typeEnum)}
                    </div>
                  ) : (
                    <Avatar className="w-10 h-10 mr-3 flex-shrink-0 border border-border shadow-sm">
                      <AvatarImage
                        src={notification.actionerProfilePictureUrl || undefined}
                        alt={notification.actionerName || ''}
                      />
                      <AvatarFallback>
                        {notification.actionerName?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <div className="flex-1 overflow-hidden mr-3 min-w-0">
                    {/* Display message with the notification content (already contains actioner's name) */}
                    <p
                      className={cn(
                        'text-[0.85rem] leading-snug mb-1 break-words',
                        notification.isRead
                          ? 'font-normal opacity-80'
                          : 'font-semibold'
                      )}
                    >
                      {notification.message}
                    </p>

                    {/* Job notification: show job title & company */}
                    {isJobNotification(notification.typeEnum) && notification.relatedJobTitle && (
                      <p className="text-xs text-muted-foreground leading-tight mb-1 opacity-85 line-clamp-1">
                        {notification.relatedJobCompany
                          ? `${notification.relatedJobTitle} at ${notification.relatedJobCompany}`
                          : notification.relatedJobTitle}
                      </p>
                    )}

                    {/* Comment notification: show original comment */}
                    {!isJobNotification(notification.typeEnum) && notification.originalCommentText && (
                      <p className="text-xs text-muted-foreground leading-tight mb-1 italic opacity-85 line-clamp-1">
                        {`Your comment: "${truncateText(notification.originalCommentText, 60)}"`}
                      </p>
                    )}

                    {/* Comment notification: show reply text */}
                    {notification.typeEnum === NotificationType.CommentReply &&
                      notification.actionerCommentText && (
                        <p className="text-xs text-muted-foreground leading-tight mb-1 italic opacity-85 line-clamp-1">
                          {`Reply: "${truncateText(notification.actionerCommentText, 60)}"`}
                        </p>
                      )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdOnUtc), {
                          addSuffix: true,
                        })}
                      </span>

                      {(() => {
                        const badge = getNotificationBadge(notification.typeEnum);
                        return (
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-xl text-[0.7rem] font-medium',
                              badge.colorClass
                            )}
                          >
                            <span className="mr-1 text-[0.85rem]">
                              {getNotificationIcon(notification.typeEnum)}
                            </span>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Delete button - fixed position and no transition on width/height */}
                  <button
                    type="button"
                    onClick={(e) => handleDeleteNotification(e, notification.id)}
                    className={cn(
                      'flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md',
                      'text-muted-foreground transition-[opacity,color] duration-150',
                      'hover:text-destructive hover:opacity-100',
                      hoveredNotification === notification.id ? 'opacity-85' : 'opacity-35'
                    )}
                  >
                    {deletingNotification === notification.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </button>
            ))
          ) : (
            <div className="p-8 text-center">
              <div className="text-3xl mb-2 opacity-60">🔔</div>
              <p className="font-medium text-[0.95rem] tracking-tight text-muted-foreground">
                No notifications
              </p>
              <p className="text-xs text-muted-foreground mt-1 opacity-80">
                We&apos;ll notify you when something happens
              </p>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};
